<template>
  <b-modal
    modal-class="document-modal form-group-view-modal"
    v-model="editorShown"
    size="xl"
    no-close-on-backdrop
    hide-header
    hide-footer
    centered
  >
    <div class="d-flex h-100">
      <div class="sidebar-forms-container bg-extra-light-gray">
        <template v-if="formGroup">
          <div class="font-weight-bold px-3 my-3" style="font-size:16px;">
            {{ formGroupTitle }}
          </div>
          <div class="sidebar-forms">
            <div
              v-for="form in pairedForms"
              :key="form.id"
              @click="setForm(form.id)"
              class="sidebar-menu-form-item d-flex align-items-center"
              :class="currentForm && form.id == currentForm.id ? 'active' : ''"
            >
              <span
                class="status-circle"
                :class="getFormDocumentColor(form.id)"
              />
              <span
                class="ml-2 no-wrap-text"
                :id="'form-group-tooltip-' + form.id"
                style="font-size: 0.925em;"
              >
                <b-tooltip
                  :target="'form-group-tooltip-' + form.id"
                  placement="bottom"
                  triggers="hover"
                >
                  <small>{{ form.name }}</small>
                </b-tooltip>
                {{ form.name }}
              </span>
            </div>
          </div>
          <div
            class="d-flex flex-column align-items-center my-3"
            style="gap: 10px"
          >
            <template v-if="groupHtml">
              <b-button
                pill
                variant="outline-primary"
                style="width: 125px"
                @click="() => showReorderModal(sendFax, 'Fax All')"
              >
                Fax All
              </b-button>
              <b-button
                pill
                variant="outline-primary"
                style="width: 125px"
                :disabled="downloadingPDF"
                @click="() => showReorderModal(savePdfGroup, 'Download All')"
              >
                Download All
              </b-button>
              <b-button
                pill
                variant="outline-primary"
                style="width: 125px"
                @click="() => showReorderModal(printGroup, 'Print All')"
              >
                Print All
              </b-button>
            </template>
          </div>
        </template>
      </div>
      <div
        class="main-container d-flex flex-column bg-white border-radius-top border-radius-bottom"
        :style="{
          width: 'calc(100% - 600px)',
          zIndex: 2
        }"
      >
        <div
          class="d-flex flex-row justify-content-between align-items-center bg-purple text-white border-radius-top"
          style="padding: 10px 20px;"
        >
          <div>
            <h5 class="modal-title font-weight-bold mb-1">{{ modalTitle }}</h5>
            <div class="modal-subtitle">{{ modalSubtitle }}</div>
          </div>
          <button
            type="button"
            aria-label="Close"
            class="close text-white"
            style="opacity:1 !important"
            @click="closeModal"
          >
            ×
          </button>
        </div>
        <div class="text-center h-100" v-if="document">
          <zoom-area ref="documentZoomArea" :hideControls="true" width="1025px">
            <vue-html2pdf
              :show-layout="false"
              :float-layout="false"
              :pdf-quality="2"
              :pdf-content-width="'100%'"
              :paginate-elements-by-height="1500"
              class="w-100"
            >
              <template slot="pdf-content">
                <div
                  v-if="singleHtml"
                  v-html-sanitize="singleHtml"
                  :id="document.documentId"
                  class="m-4"
                />
              </template>
            </vue-html2pdf>
          </zoom-area>
        </div>
        <div
          class="d-flex justify-content-between align-items-center w-100 bg-extra-light-gray position-relative border-top mt-auto border-top border-radius-bottom"
          style="padding: 10px 20px;"
        >
          <div>
            <template v-if="singleHtml">
              <b-button
                v-if="
                  document.status != 'Completed' &&
                    hasCurrentUserDocumentPermission(
                      document.form.id,
                      'hasEntryAccess'
                    )
                "
                class="mr-2"
                variant="primary"
                @click="editDocument"
              >
                Edit
              </b-button>
              <b-button class="mr-2" variant="primary" @click="printSingle">
                Print
              </b-button>
              <b-button
                v-if="document"
                class="mr-2"
                variant="primary"
                :disabled="downloadingPDF"
                @click="savePdfSingle"
              >
                Download
              </b-button>
              <b-button
                v-if="document"
                class="mr-2"
                variant="primary"
                @click="sendFax(false)"
              >
                Fax
              </b-button>
            </template>
          </div>
          <div style="font-size: 20px">
            <i
              @click="addDocumentZoomArea()"
              class="fa fa-search-plus zoom-control hover-text-primary"
            ></i>
            <i
              @click="lessDocumentZoomArea()"
              class="fa fa-search-minus zoom-control hover-text-primary"
            ></i>
          </div>
          <div style="width: 236px; text-align: right;">
            <b-button @click="closeModal" variant="secondary">
              Close
            </b-button>
          </div>
        </div>
      </div>
      <div class="sidebar">
        <div class="sidebar-inner h-100 my-2">
          <div class="d-flex p-2 mb-3">
            <div
              class="sidebar-tab-title px-3 text-primary border-bottom border-primary"
            >
              NOTES
            </div>
          </div>
          <div class="position-relative d-flex h-100">
            <document-notes-tab-content
              :documentId="document && document.documentId"
              @onChange="refetchDocuments = true"
            />
          </div>
        </div>
      </div>
    </div>
    <vue-html2pdf
      ref="html2PdfSingle"
      :show-layout="false"
      :float-layout="false"
      :enable-download="true"
      :pdf-quality="2"
      :pdf-content-width="'1025px'"
      :paginate-elements-by-height="1500"
      :html-to-pdf-options="pdfSingle"
      class="position-fixed w-100"
      style="top: 1000%"
      @hasDownloaded="hasDownloaded"
    >
      <template slot="pdf-content"> </template>
    </vue-html2pdf>
    <vue-html2pdf
      ref="html2PdfGroup"
      :show-layout="false"
      :float-layout="false"
      :enable-download="true"
      :pdf-quality="2"
      :pdf-content-width="'1025px'"
      :paginate-elements-by-height="1500"
      :manual-pagination="true"
      :html-to-pdf-options="pdfGroup"
      class="position-fixed w-100"
      style="top: 1000%"
      @hasDownloaded="hasDownloaded"
    >
      <template slot="pdf-content"> </template>
    </vue-html2pdf>
    <template v-if="eoc && formGroup && documentGroup">
      <document-group-add-edit-modal
        v-if="showDocumentGroupEditor"
        v-model="showDocumentGroupEditor"
        :formGroupId="formGroupId"
        :submitId="documentGroupId"
        :eocId="eoc.id"
        :clientId="eoc.patient.id"
        :initialFormId="currentForm.id"
        @onModify="onModify"
      />
    </template>
    <send-fax-modal
      v-if="showFaxModal"
      v-model="showFaxModal"
      :ids="this.ids"
      :sendFaxMethod="this.sendFaxMethod"
    ></send-fax-modal>
    <document-reorder
      v-if="documentReorderModal.show"
      v-model="documentReorderModal.show"
      :documents="documentReorderModal.documents"
      :actionLabel="documentReorderModal.actionLabel"
      @onModify="onModifyDocumentReorder"
      @onReorder="onReorder"
    />
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import eocServer from "../../../server/eoc";
import documentServer from "../../../server/document";
import scheduleEventServer from "../../../server/scheduleEvent";
import formSubmitterServer from "../../../server/formSubmitter";
import documentGroupServer from "../../../server/documentGroup";
import VueHtml2pdf from "vue-html2pdf";
import ZoomArea from "../../controls/ZoomArea";
import DocumentGroupAddEditModal from "./DocumentGroupAddEditModal.vue";
import DocumentNotesTabContent from "../document/DocumentNotesTabContent.vue";
import SendFaxModal from "../../fax/SendFaxModal.vue";
import DocumentReorder from "../document/DocumentReorder.vue";

const getFormSubmitId = (results, formId) => {
  for (let index = 0; index < results.length; index++) {
    const result = results[index];
    if (result.formId == formId) return result.documentId;
  }
  return null;
};

export default {
  name: "DocumentGroupViewModal",
  components: {
    VueHtml2pdf,
    ZoomArea,
    DocumentNotesTabContent,
    DocumentGroupAddEditModal,
    SendFaxModal,
    DocumentReorder
  },
  props: ["formGroupId", "documentGroupId"],
  data() {
    return {
      ids: [],
      sendFaxMethod: "",
      eoc: null,
      formGroup: null,
      documentGroup: null,
      document: null,
      currentForm: null,
      singleUrl: null,
      singleHtml: null,
      singleBlobUrl: null,
      groupUrl: null,
      groupHtml: null,
      groupBlobUrl: null,
      downloadingPDF: false,
      showDocumentGroupEditor: false,
      refetchDocuments: false,
      showFaxModal: false,
      reorderedDocuments: [],
      documentReorderModal: {
        show: false,
        documents: null,
        callback: () => {},
        actionLabel: ""
      }
    };
  },
  async mounted() {
    this.formGroup = await this.getFormGroupById(this.formGroupId);
    this.documentGroup = await this.getFormGroupSubmitById(
      this.documentGroupId
    );
    this.eoc = await eocServer.get(this.documentGroup.episodeOfCareId);

    await this.setForm(this.documentGroup.results[0].formId);
    this.reorderedDocuments = this.pairedForms.map(form => {
      let documentId = "";
      for (let index = 0; index < this.documentGroup.results.length; index++) {
        const result = this.documentGroup.results[index];
        if (result.formId == form.id) {
          documentId = result.documentId;
        }
      }
      return {
        value: documentId,
        label: `${form.name} `
      };
    });
  },
  methods: {
    ...mapActions("layout", ["show", "hide"]),
    async setForm(id) {
      this.currentForm = await this.getFormBuilderFormById(id);

      const documentId = this.documentGroup
        ? getFormSubmitId(this.documentGroup.results, this.currentForm.id)
        : null;

      this.document = documentId
        ? await formSubmitterServer.getDetailedImproved(documentId)
        : null;

      this.loadSingle();
      this.loadGroup();
    },
    loadSingle() {
      if (!this.document) {
        return;
      }

      if (this.document.mode == "File") {
        throw "Files can't be opened from this viewer";
      }

      if (this.document.category == "groupSession") {
        this.singleUrl = scheduleEventServer.groupSessionDocumentViewUrl(
          this.document.documentId
        );
      } else if (this.document.category == "masterTreatmentPlan") {
        this.singleUrl = documentServer.treatmentPlanDocumentViewUrl(
          this.document.documentId
        );
      } else {
        this.singleUrl = documentServer.documentsViewUrl([
          this.document.documentId
        ]);
      }

      fetch(this.singleUrl.url, { headers: this.singleUrl.headers }).then(
        response => {
          const clone = response.clone();

          response.blob().then(blob => {
            this.singleBlobUrl = URL.createObjectURL(blob);
          });

          clone.text().then(html => {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, "text/html");
            this.singleHtml = doc.body.innerHTML.replace(/vh-100/gi, "");

            const content = this.$refs.html2PdfSingle.$el.querySelector(
              ".content-wrapper"
            );
            const fragment = document
              .createRange()
              .createContextualFragment(this.singleHtml);

            content.appendChild(fragment);

            requestAnimationFrame(() => {
              Array.from(content.querySelectorAll(".document-single")).forEach(
                singleDoc => {
                  const rect = singleDoc.getBoundingClientRect();
                  singleDoc.style.transform = "scale(0.6925)";
                  singleDoc.style.transformOrigin = "0% 0% 0px";
                  singleDoc.style.height = `${rect.height * 0.6925}px`;
                }
              );
            });
          });
        }
      );
    },
    loadGroup() {
      const ids = this.documentGroup.results.map(u => u.documentId);
      this.groupUrl = documentServer.documentsViewUrl(ids);

      fetch(this.groupUrl.url, { headers: this.groupUrl.headers }).then(
        response => {
          const clone = response.clone();

          response.blob().then(blob => {
            this.groupBlobUrl = URL.createObjectURL(blob);
          });

          clone.text().then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            this.groupHtml = doc.body.innerHTML.replace(/vh-100/gi, "");

            const content = this.$refs.html2PdfGroup.$el.querySelector(
              ".content-wrapper"
            );
            const fragment = document
              .createRange()
              .createContextualFragment(this.groupHtml);

            content.appendChild(fragment);

            requestAnimationFrame(() => {
              Array.from(content.querySelectorAll(".document-single")).forEach(
                singleDoc => {
                  const rect = singleDoc.getBoundingClientRect();
                  singleDoc.style.transform = "scale(0.6925)";
                  singleDoc.style.transformOrigin = "0% 0% 0px";
                  singleDoc.style.height = `${rect.height * 0.6925}px`;
                }
              );
            });
          });
        }
      );
    },
    getFormDocumentColor(formId) {
      if (!this.documentGroup) {
        return "";
      }

      for (let index = 0; index < this.documentGroup.results.length; index++) {
        const result = this.documentGroup.results[index];
        if (result.formId == formId) {
          if (result.status == "Completed") return "success";
          if (result.status == "Pending") return "warning";
          return "draft";
        }
      }
    },
    editDocument() {
      this.showDocumentGroupEditor = true;
    },
    sendFax(allDocuments = true) {
      this.sendFaxMethod = "sendDocumentsPdf";
      this.ids = this.reorderedDocuments.map(doc => doc.value);
      if (!allDocuments) {
        this.ids = [this.document.documentId];
      }
      if (this.document.category == "masterTreatmentPlan") {
        this.sendFaxMethod = "sendTreatmentPlanPdf";
        this.ids = [this.document.documentId];
      }
      this.showFaxModal = true;
    },
    savePdfSingle() {
      let downloadMethod = documentServer.downloadDocumentPdf;
      let ids = [this.document.documentId];
      if (this.document.category == "masterTreatmentPlan") {
        downloadMethod = documentServer.downloadtreatmentPlanPdf;
        ids = this.document.documentId;
      }
      this.downloadingPDF = true;
      const filename = this.pdfSingle.filename;

      this.show();
      downloadMethod(ids)
        .then(res => {
          res.blob().then(blob => {
            let fileURL = URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.href = fileURL;
            link.download = filename;
            link.dispatchEvent(new MouseEvent("click"));
          });
        })
        .finally(() => {
          this.hide();
          this.downloadingPDF = false;
        });
    },
    onReorder({ reorderedDocuments }) {
      this.reorderedDocuments = reorderedDocuments;
      this.documentReorderModal.callback();
    },
    onModifyDocumentReorder() {
      this.documentReorderModal = {
        show: false,
        documents: [],
        callback: () => {},
        actionLabel: ""
      };
    },
    showReorderModal(callback, actionLabel) {
      this.documentReorderModal = {
        show: true,
        documents: this.reorderedDocuments,
        callback,
        actionLabel
      };
    },
    savePdfGroup() {
      this.downloadingPDF = true;
      const filename = this.pdfSingle.filename;

      this.show();
      documentGroupServer
        .downloadDocumentGroupPdf(
          this.documentGroupId,
          this.reorderedDocuments.map(doc => doc.value)
        )
        .then(res => {
          res.blob().then(blob => {
            let fileURL = URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.href = fileURL;
            link.download = filename;
            link.dispatchEvent(new MouseEvent("click"));
          });
        })
        .finally(() => {
          this.downloadingPDF = false;
          this.hide();
        });
    },
    printSingle() {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.zIndex = -100;
      iframe.style.width = "2250px";
      iframe.src = this.singleBlobUrl;
      iframe.onload = function() {
        iframe.contentWindow.onafterprint = function() {
          document.body.removeChild(iframe);
        };

        setTimeout(() => {
          iframe.contentWindow.print();
        }, 500);
      };

      document.body.appendChild(iframe);
    },
    printGroup() {
      this.groupUrl = documentServer.documentsViewUrl(
        this.reorderedDocuments.map(d => d.value)
      );
      fetch(this.groupUrl.url, { headers: this.groupUrl.headers }).then(
        response => {
          response.blob().then(blob => {
            this.groupBlobUrl = URL.createObjectURL(blob);
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.zIndex = -100;
            iframe.style.width = "2250px";
            iframe.src = this.groupBlobUrl;
            iframe.onload = function() {
              iframe.contentWindow.onafterprint = function() {
                document.body.removeChild(iframe);
              };

              setTimeout(() => {
                iframe.contentWindow.print();
              }, 500);
            };

            document.body.appendChild(iframe);
          });
        }
      );
    },
    hasDownloaded() {
      this.downloadingPDF = false;
    },
    addDocumentZoomArea() {
      this.$refs.documentZoomArea.addZoom();
    },
    lessDocumentZoomArea() {
      this.$refs.documentZoomArea.lessZoom();
    },
    onModify(e) {
      this.showDocumentGroupEditor = false;
      this.editorShown = false;

      this.$emit("onModify", e);
    },
    closeModal() {
      this.$emit("onModify", { refetchDocuments: this.refetchDocuments });
      this.editorShown = false;
    }
  },
  computed: {
    ...mapGetters("formBuilder", ["getFormBuilderFormById"]),
    ...mapGetters("formGroupSubmitter", ["getFormGroupSubmitById"]),
    ...mapGetters("formGroup", ["getFormGroupById"]),
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    modalTitle() {
      if (!this.formGroup || !this.currentForm) {
        return "";
      }

      return `${
        this.document?.documentRefNumber
          ? "Ref# " + this.document.documentRefNumber + " | "
          : ""
      }${this.documentGroup.forms.find(u => u.id == this.currentForm.id).name}`;
    },
    modalSubtitle() {
      return this.eoc ? this.formatEocObj(this.eoc) : "";
    },
    formGroupTitle() {
      return this.formGroup ? this.formGroup.name : "";
    },
    pdfOptions() {
      return {
        image: { type: "jpeg", quality: 1 },
        margin: 12,
        html2canvas: { scale: 2.5 }
      };
    },
    pdfSingle() {
      const filename = this.document
        ? `${this.modalTitle} ${this.modalSubtitle} - ${this.formatDateTimeSql(
            this.document.effectiveDate
              ? this.document.effectiveDate
              : this.document.createdAt
          )}.pdf`
        : "";

      return { ...this.pdfOptions, filename };
    },
    pdfGroup() {
      const filename = this.formGroup
        ? `${this.formGroupTitle} ${
            this.modalSubtitle
          } - ${this.formatDateTimeSql(this.formGroup.createdAt)}.pdf`
        : "";

      return { ...this.pdfOptions, filename };
    },
    pairedForms() {
      return this.formGroup && this.documentGroup
        ? this.documentGroup.forms.filter(f => {
            const documentId = getFormSubmitId(
              this.documentGroup.results,
              f.id
            );
            return !!documentId;
          })
        : [];
    }
  }
};
</script>

<style scoped>
.sidebar {
  width: 350px;
  background-color: #f5f8fc;
  display: flex;
  flex-direction: column;
  z-index: 1;
  border-top-right-radius: 0.3rem;
  border-bottom-right-radius: 0.3rem;
  margin-top: 10px;
  margin-bottom: 10px;
}
.sidebar-inner {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 350px;
  padding: 15px;
}
.sidebar-tab-title {
  border-bottom-width: 3px !important;
  width: 176px;
  font-size: 14px;
  padding-bottom: 10px;
  text-align: center;
  flex-grow: 1;
}
.border-radius-top {
  border-top-right-radius: 0.3rem;
  border-top-left-radius: 0.3rem;
}
.border-radius-bottom {
  border-bottom-right-radius: 0.3rem;
  border-bottom-left-radius: 0.3rem;
}
.main-container {
  -webkit-box-shadow: -10px 0px 13px -7px #000000a6,
    10px 0px 13px -7px #000000a6, 5px 5px 15px 5px rgba(0, 0, 0, 0);
  box-shadow: -10px 0px 13px -7px #000000a6, 10px 0px 13px -7px #000000a6,
    5px 5px 15px 5px rgba(0, 0, 0, 0);
}
</style>

<style>
.form-group-view-modal .modal-content {
  border: none;
  border-radius: 30px;
}
.document-modal .modal-content {
  background-color: transparent;
}
</style>
